import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Listing, ListingData, SearchListingsRequest } from './types';
import { useService } from '../../contexts/ServiceContext';
import { useListingSearch } from './hooks/useListingSearch';
import { parseSearchParams, buildSearchParams } from './utils';

/**
 * Main hook for the Overview page.
 * Handles WebSocket listing search with pagination, URL sync, and progressive loading.
 * Falls back to sample data when WebSocket is unavailable.
 */
export const useOverview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isWebSocketReady } = useService();

  // WebSocket listing search
  const {
    listings: wsListings,
    pagination,
    isLoadingSummary,
    isLoadingPage,
    loadedDetailsCount,
    totalExpectedDetails,
    error: wsError,
    executeSearch,
  } = useListingSearch();

  // Parse URL parameters to get initial state
  const urlState = parseSearchParams(searchParams);

  // Determine if we're using WebSocket
  const usingWebSocket = isWebSocketReady();

  // Track if initial URL sync has happened
  const [hasInitializedURL, setHasInitializedURL] = useState(false);

  // Update URL parameters
  const updateURL = useCallback(
    (newState: {
      page: number;
      perPage: number;
      filters: {
        propertyType: string;
        status: string;
        search: string;
      };
    }) => {
      const params = buildSearchParams(newState);
      const queryString = params.toString();
      const newPath = queryString ? `?${queryString}` : '';

      // Use replace for filter changes (page 1), push for pagination
      navigate(newPath, { replace: newState.page === 1 });
    },
    [navigate],
  );

  // Update filters
  const updateFilters = useCallback(
    (newFilters: {
      propertyType?: string;
      status?: string;
      search?: string;
    }) => {
      const newState = {
        page: 1, // Reset to page 1 when filters change
        perPage: urlState.perPage,
        filters: {
          ...urlState.filters,
          ...newFilters,
        },
      };
      updateURL(newState);
    },
    [urlState, updateURL],
  );

  // Go to specific page
  const goToPage = useCallback(
    (page: number) => {
      const newState = {
        ...urlState,
        page,
      };
      updateURL(newState);
    },
    [urlState, updateURL],
  );

  // Change items per page
  const changePerPage = useCallback(
    (perPage: number) => {
      const newState = {
        page: 1, // Reset to page 1 when changing per page
        perPage,
        filters: urlState.filters,
      };
      updateURL(newState);
    },
    [urlState.filters, updateURL],
  );

  // Sync URL with default values on initial load
  useEffect(() => {
    if (!hasInitializedURL) {
      const currentParams = buildSearchParams(urlState);
      const currentParamsString = currentParams.toString();
      const urlParamsString = searchParams.toString();

      // Only update if the params are different (meaning defaults were applied)
      if (currentParamsString !== urlParamsString) {
        const newPath = currentParamsString ? `?${currentParamsString}` : '';
        navigate(newPath, { replace: true });
      }
      setHasInitializedURL(true);
    }
  }, [hasInitializedURL, navigate, searchParams, urlState]);

  // Execute search when URL changes
  useEffect(() => {
    if (usingWebSocket) {
      // Use WebSocket search
      const request: SearchListingsRequest = {
        page: urlState.page,
        perPage: urlState.perPage,
        filters: {
          propertyType: urlState.filters.propertyType || undefined,
          mlsStatus: urlState.filters.status || undefined,
          search: urlState.filters.search || undefined,
        },
      };

      executeSearch(request);
    }
  }, [
    urlState.page,
    urlState.perPage,
    urlState.filters.propertyType,
    urlState.filters.status,
    urlState.filters.search,
    usingWebSocket,
    executeSearch,
  ]);

  return {
    listings: wsListings as (Listing | ListingData)[],
    loading: isLoadingSummary,
    isLoadingPage,
    loadedDetailsCount,
    totalExpectedDetails,
    error: wsError,
    filters: urlState.filters,
    currentPage: urlState.page,
    perPage: urlState.perPage,
    totalCount: pagination?.total ?? null,
    hasNextPage: pagination?.hasNext ?? false,
    hasPrevPage: pagination?.hasPrev ?? false,
    updateFilters,
    goToPage,
    changePerPage,
    refetch: () =>
      executeSearch({
        page: urlState.page,
        perPage: urlState.perPage,
        filters: {
          propertyType: urlState.filters.propertyType || undefined,
          mlsStatus: urlState.filters.status || undefined,
          search: urlState.filters.search || undefined,
        },
      }),
    isUsingWebSocket: true,
  };
};
